export type MessageTree = {
  [key: string]: string | MessageTree;
};

export type DeepPartialMessageTree = {
  [key: string]: string | DeepPartialMessageTree | undefined;
};

export type MessageParams = Record<string, string | number | boolean | null | undefined>;
export type MessageDescriptor = { id: string };
export type FormatMessageLike = (descriptor: MessageDescriptor, values?: MessageParams) => string;

type IntlListFormatter = {
  format(items: Iterable<string>): string;
};

type IntlListFormatConstructor = new (
  locales?: string | readonly string[],
  options?: { style?: "long" | "short" | "narrow"; type?: "conjunction" | "disjunction" | "unit" },
) => IntlListFormatter;

export function createI18nRuntime<AppLocale extends string>({
  defaultLocale,
  localeCatalogs,
  normalizeLocale,
  localeState,
}: {
  defaultLocale: AppLocale;
  localeCatalogs: Record<AppLocale, MessageTree>;
  normalizeLocale: (locale?: string | null) => AppLocale;
  localeState?: {
    getLocale: () => AppLocale;
    setLocale: (locale: string) => AppLocale;
    subscribeLocaleChange: (listener: (locale: AppLocale) => void) => () => void;
  };
}) {
  type LocaleListener = (locale: AppLocale) => void;
  const localeListeners = new Set<LocaleListener>();
  let currentLocale: AppLocale = defaultLocale;

  function mergeMessageTrees(base: MessageTree, overrides?: DeepPartialMessageTree): MessageTree {
    if (!overrides) {
      return base;
    }
    const result: MessageTree = { ...base };
    for (const [key, overrideValue] of Object.entries(overrides)) {
      if (overrideValue === undefined) {
        continue;
      }
      const baseValue = result[key];
      if (typeof overrideValue === "string" || typeof baseValue === "string" || !baseValue) {
        result[key] = overrideValue as string | MessageTree;
        continue;
      }
      result[key] = mergeMessageTrees(baseValue, overrideValue as DeepPartialMessageTree);
    }
    return result;
  }

  function getMessageFromTree(tree: MessageTree, path: string) {
    const segments = path.split(".");
    let current: string | MessageTree = tree;
    for (const segment of segments) {
      if (typeof current === "string" || !(segment in current)) {
        return undefined;
      }
      const next: string | MessageTree | undefined = current[segment];
      if (next === undefined) {
        return undefined;
      }
      current = next;
    }
    return typeof current === "string" ? current : undefined;
  }

  function resolveMessages(locale?: string) {
    if (locale) {
      return localeCatalogs[normalizeLocale(locale)];
    }
    return localeCatalogs[localeState?.getLocale() ?? currentLocale];
  }

  function setLocale(locale: string) {
    if (localeState) return localeState.setLocale(locale);
    const nextLocale = normalizeLocale(locale);
    if (nextLocale === currentLocale) {
      return currentLocale;
    }
    currentLocale = nextLocale;
    localeListeners.forEach((listener) => listener(nextLocale));
    return nextLocale;
  }

  function getLocale() {
    if (localeState) return localeState.getLocale();
    return currentLocale;
  }

  function subscribeLocaleChange(listener: LocaleListener) {
    if (localeState) return localeState.subscribeLocaleChange(listener);
    localeListeners.add(listener);
    return () => {
      localeListeners.delete(listener);
    };
  }

  function hasMessage(path: string, locale?: string) {
    return getMessageFromTree(resolveMessages(locale), path) !== undefined;
  }

  function t(path: string, params?: MessageParams, locale?: string) {
    const template = getMessageFromTree(resolveMessages(locale), path) ?? path;
    let positionalIndex = 0;
    return template.replace(/\$\{[^{}]*\}|\{(\w+)\}/g, (_match, namedKey: string | undefined) => {
      const key = namedKey ?? `p${positionalIndex++}`;
      const value = params?.[key];
      return value === undefined || value === null ? "" : String(value);
    });
  }

  function formatMessage(descriptor: MessageDescriptor, values?: MessageParams, locale?: string) {
    return t(descriptor.id, values, locale);
  }

  function createMessageProxy<T extends object>(basePath: string): T {
    return new Proxy(
      {},
      {
        get(_target, property) {
          if (typeof property !== "string") {
            return undefined;
          }
          const nextPath = basePath ? `${basePath}.${property}` : property;
          if (hasMessage(nextPath)) {
            return t(nextPath);
          }
          return createMessageProxy(nextPath);
        },
      },
    ) as T;
  }

  function formatEnumPath(path: string, value?: string | null, fallback?: string, locale?: string) {
    if (!value) {
      return fallback ?? "";
    }
    const labelPath = `${path}.${value}`;
    const label = t(labelPath, undefined, locale);
    return label === labelPath ? (fallback ?? value) : label;
  }

  function formatEnumLabel<T extends string>(
    labels: Partial<Record<T, string>>,
    value?: string | null,
    fallback?: string,
  ) {
    if (!value) {
      return fallback ?? "";
    }
    return labels[value as T] ?? fallback ?? value;
  }

  function formatBooleanLabel(value: boolean) {
    return value ? t("common.boolean.yes") : t("common.boolean.no");
  }

  function formatLocalizedList(values: readonly unknown[], locale?: string | null) {
    const items = values
      .map((value) => String(value ?? "").trim())
      .filter((value) => value.length > 0);
    if (items.length <= 1) {
      return items[0] ?? "";
    }
    const normalizedLocale = locale == null ? getLocale() : normalizeLocale(locale);
    const ListFormat = (Intl as typeof Intl & { ListFormat?: IntlListFormatConstructor }).ListFormat;
    if (!ListFormat) {
      return items.join(", ");
    }
    return new ListFormat(normalizedLocale, { style: "long", type: "conjunction" }).format(items);
  }

  function formatActionObjectLabel(actionLabel: string, objectLabel: string, locale?: string | null) {
    const normalizedLocale = normalizeLocale(locale);
    if (normalizedLocale === "ja") {
      return `${objectLabel}を${actionLabel}`;
    }
    if (normalizedLocale === "en") {
      return `${actionLabel} ${objectLabel}`.trim();
    }
    return `${actionLabel}${objectLabel}`.trim();
  }

  function formatActionObjectMessage(
    actionId: string,
    objectId: string,
    locale?: string | null,
    actionValues?: MessageParams,
    objectValues?: MessageParams,
  ) {
    return formatActionObjectLabel(
      t(actionId, actionValues, locale ?? undefined),
      t(objectId, objectValues, locale ?? undefined),
      locale,
    );
  }

  function formatActionObjectIntlLabel(
    formatMessageLike: FormatMessageLike,
    locale: string | null | undefined,
    actionId: string,
    objectId: string,
    actionValues?: MessageParams,
    objectValues?: MessageParams,
  ) {
    return formatActionObjectLabel(
      formatMessageLike({ id: actionId }, actionValues),
      formatMessageLike({ id: objectId }, objectValues),
      locale,
    );
  }

  return {
    messageCatalogs: localeCatalogs,
    mergeMessageTrees,
    normalizeLocale,
    setLocale,
    getLocale,
    subscribeLocaleChange,
    createMessageProxy,
    hasMessage,
    t,
    formatMessage,
    formatEnumPath,
    formatEnumLabel,
    formatBooleanLabel,
    formatLocalizedList,
    formatActionObjectLabel,
    formatActionObjectMessage,
    formatActionObjectIntlLabel,
  };
}
